import type { CheckoutConfig, CheckoutConfigProps } from 'models';
import { DisplayMode } from 'models';
import { postMessageService } from 'services/postMessageService';
import { addEventListenerOnce } from 'utils/addEventListenerOnce/addEventListenerOnce';
import { decodeObject, encodeObject } from 'utils/encoding/encoding';
import { windowUtils } from 'utils/windowUtils/windowUtils';
import zoid from 'zoid';

declare global {
  interface Window {
    flywire: {
      Checkout: {
        render: (
          initialConfig: CheckoutConfig | Record<string, never>,
          selector: string,
          type: string,
        ) => void;
      };
    };
    requester: string;
    xprops: {
      close: () => void;
      env: string;
      fwClose: () => void;
      idleTimeout: number;
    };
  }
}

if (!window.flywire) {
  const checkout = zoid.create({
    tag: 'fw-checkout',
    dimensions: {
      width: '100%',
      height: '100%',
    },
    url: (config: CheckoutConfig) => {
      const urls = {
        demo: 'https://payment.demo.flywire.com/checkout',
        local: 'http://localhost:5400/checkout',
        integration: 'http://localhost:5400/checkout',
        app: 'http://checkout/checkout',
        production: 'https://payment.flywire.com/checkout',
        staging: 'https://payment.flywire.lol/checkout',
      };

      const { props } = decodeObject<CheckoutConfig>(config) as {
        props: CheckoutConfigProps;
      };
      return (
        urls[props.env] ||
        `https://payment-dev-${props.env}.flywire.cc/checkout`
      );
    },
    defaultEnv: 'development',
    defaultLogLevel: 'error',
  });
  window.requester = 'Embedded 2.0';
  window.flywire = {
    Checkout: {
      render: (initialConfig = {}, selector, type = 'click') => {
        let _config = initialConfig;

        const createOverlay = () => {
          const overlay = document.createElement('div');
          overlay.id = 'flywire-checkout';
          overlay.setAttribute(
            'style',
            'position: fixed; display: block; width: 100%; height: 100%; top: 0; left: 0; right: 0; bottom:0; background-color: rgba(41,51,60,0.9); z-index: 9999;',
          );

          (document.querySelector('body') as HTMLElement).appendChild(overlay);
          return overlay;
        };

        const createDrawer = () => {
          const drawer = document.createElement('div');
          drawer.id = 'flywire-checkout-drawer';

          const isMobile = window.innerWidth <= 768;

          if (isMobile) {
            drawer.setAttribute(
              'style',
              'position: fixed; bottom: 0; left: 0; width: 100%; height: 95vh; background-color: white; z-index: 9999; transform: translateY(100%); transition: transform 0.3s ease-in-out; border-top: 1px solid #e0e0e0; border-radius: 16px 16px 0 0;',
            );
          } else {
            drawer.setAttribute(
              'style',
              'position: fixed; top: 0; right: 0; width: 450px; height: 100%; background-color: white; z-index: 9999; transform: translateX(100%); transition: transform 0.3s ease-in-out; border-left: 1px solid #e0e0e0;',
            );
          }

          const backdrop = document.createElement('div');
          backdrop.id = 'flywire-drawer-backdrop';
          backdrop.setAttribute(
            'style',
            'position: fixed; top: 0; left: 0; width: 100%; height: 100%; background-color: rgba(0,0,0,0.5); z-index: 9998; opacity: 0; transition: opacity 0.3s ease-in-out;',
          );

          (document.querySelector('body') as HTMLElement).appendChild(backdrop);
          (document.querySelector('body') as HTMLElement).appendChild(drawer);

          setTimeout(() => {
            if (isMobile) {
              drawer.style.transform = 'translateY(0)';
            } else {
              drawer.style.transform = 'translateX(0)';
            }
            backdrop.style.opacity = '1';
          }, 10);

          const handleResize = () => {
            const newIsMobile = window.innerWidth <= 768;

            if (newIsMobile !== isMobile) {
              if (newIsMobile) {
                drawer.setAttribute(
                  'style',
                  'position: fixed; bottom: 0; left: 0; width: 100%; height: 95vh; background-color: white; z-index: 9999; transform: translateY(0); transition: transform 0.3s ease-in-out; border-top: 1px solid #e0e0e0; border-radius: 16px 16px 0 0;',
                );
              } else {
                drawer.setAttribute(
                  'style',
                  'position: fixed; top: 0; right: 0; width: 450px; height: 100%; background-color: white; z-index: 9999; transform: translateX(0); transition: transform 0.3s ease-in-out; border-left: 1px solid #e0e0e0;',
                );
              }
            }
          };

          window.addEventListener('resize', handleResize);

          return { drawer, backdrop, handleResize };
        };

        const openModal = () => {
          const overlay = createOverlay();
          const cfg = Object.assign({}, _config);

          const body = document.querySelector('body') as HTMLElement;
          const originalOverflow = body.style.overflow;
          body.style.overflow = 'hidden';

          const removeOverlay = () => {
            body.removeChild(overlay);
            body.style.overflow = originalOverflow;
            window.xprops.close();
          };

          cfg.fwClose = () => {
            postMessageService.sendCancellation(_config);
            removeOverlay();
          };

          cfg.fwFinish = (returnUrl, forceRedirectInNewWindow = false) => {
            if (_config.return_type === 'postMessage') {
              postMessageService.sendFinish(returnUrl, {
                ..._config,
                forceRedirectInNewWindow,
              });
              removeOverlay();
              return;
            }
            windowUtils.redirectTo(returnUrl);
          };

          checkout(encodeObject(cfg)).render(overlay);
        };

        const openDrawer = () => {
          const { drawer, backdrop, handleResize } = createDrawer();
          const cfg = Object.assign({}, _config);

          const body = document.querySelector('body') as HTMLElement;
          const originalOverflow = body.style.overflow;
          body.style.overflow = 'hidden';

          const removeDrawer = () => {
            const isMobile = window.innerWidth <= 768;

            if (isMobile) {
              drawer.style.transform = 'translateY(100%)';
            } else {
              drawer.style.transform = 'translateX(100%)';
            }
            backdrop.style.opacity = '0';
            setTimeout(() => {
              window.removeEventListener('resize', handleResize);
              body.removeChild(drawer);
              body.removeChild(backdrop);
              body.style.overflow = originalOverflow;
            }, 300);
            window.xprops?.close();
          };

          cfg.fwClose = () => {
            postMessageService.sendCancellation(_config);
            removeDrawer();
          };

          cfg.fwFinish = (returnUrl, forceRedirectInNewWindow = false) => {
            if (_config.return_type === 'postMessage') {
              postMessageService.sendFinish(returnUrl, {
                ..._config,
                forceRedirectInNewWindow,
              });
              removeDrawer();
              return;
            }
            window.location.assign(returnUrl);
          };

          checkout(encodeObject(cfg)).render(drawer);
        };

        const openInline = (container: HTMLElement) => {
          const cfg = Object.assign({}, _config);

          cfg.fwClose = () => {
            postMessageService.sendCancellation(_config);
          };

          cfg.fwFinish = returnUrl => {
            if (_config.return_type === 'postMessage') {
              postMessageService.sendFinish(returnUrl, _config);
              return;
            }
            windowUtils.redirectTo(returnUrl);
          };

          checkout(encodeObject(cfg)).render(container);

          setTimeout(() => {
            const iframes = container.querySelectorAll('iframe');
            const parentHeight = container.getBoundingClientRect().height;

            iframes.forEach(iframe => {
              if (!iframe.style.width || iframe.style.width === '') {
                iframe.style.width = '100%';
              }
              if (!iframe.style.height || iframe.style.height === '') {
                iframe.style.height =
                  parentHeight > 0 ? `${parentHeight}px` : '700px';
              }
              if (!iframe.width) {
                iframe.width = '100%';
              }
              if (!iframe.height) {
                iframe.height =
                  parentHeight > 0 ? parentHeight.toString() : '700';
              }
            });
          }, 1000);
        };

        const open = () => {
          const mode = _config.theme?.mode;

          if (mode === DisplayMode.Drawer || mode === 'drawer') {
            openDrawer();
          } else if (mode === DisplayMode.Inline || mode === 'inline') {
            const container = document.querySelector(selector) as HTMLElement;
            if (container) {
              openInline(container);
            }
          } else {
            openModal();
          }
        };

        const mode = _config.theme?.mode;

        if (mode === DisplayMode.Inline || mode === 'inline') {
          const container = document.querySelector(selector) as HTMLElement;
          if (container) {
            openInline(container);
          } else {
            console.error(`Inline mode container not found: ${selector}`);
          }
        } else {
          addEventListenerOnce(selector, type, open);
        }

        return {
          update: (newConfig = {}) => {
            _config = newConfig;
          },
        };
      },
    },
  };
}
